import { Button, Group, List, Stack, Text, TextInput, Title, Paper, createStyles } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString, normalizeOperationOutcome } from '@medplum/core';
import { Form, FormSection, getErrorsForInput, MedplumLink, useMedplum } from '@medplum/react';
import { useState } from 'react';
import { getProjectId } from '../../utils';
import { AccessPolicyInput } from '../AccessPolicy/AccessPolicyInput';
import { useNavigate } from 'react-router-dom';

const useStyles = createStyles(() => ({
  root: {
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "20px",
    paddingBottom: "20px",
    marginTop: "20px"
  },
  tab: {
    color: ""
  }
}));

export interface CreateClientProps {
  isNew: Boolean;
}

export function CreateClientPage(props: CreateClientProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate()
  const { classes } = useStyles();
  const projectId = getProjectId(medplum);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [redirectUri, setRedirectUri] = useState<string>('');
  const [accessPolicy, setAccessPolicy] = useState<any>();
  const [outcome, setOutcome] = useState<any>();
  const [client, setClient] = useState<any | undefined>(undefined);

  return (
    <>
    <Paper
        sx={{
          borderRadius: "8px",
          marginLeft: "40px",
          marginRight: "40px",
          background: "'#FFF",
          boxShadow: "0px 1px 24px 2px rgba(0, 0, 0, 0.06)",
          marginTop: "20px"
        }}
        className={classes.root}
      >
      <Title>Create new Client</Title>
      <Form
        onSubmit={() => {
          const body = {
            name,
            description,
            redirectUri,
            accessPolicy,
          };
          medplum
            .post('admin/projects/' + projectId + '/client', body)
            .then((result: any) => {
              medplum.invalidateSearches('ClientApplication');
              medplum.invalidateSearches('ProjectMembership');
              navigate(`/client/details/${result.resourceType}/${result.id}`)
              showNotification({ color: 'green', message: 'Client created' });
            })
            .catch((err) => {
              showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
              setOutcome(normalizeOperationOutcome(err));
            });
        }}
      >
        {!client && (
          <Stack>
            <FormSection title="Client Name*" htmlFor="name" outcome={outcome}>
              <TextInput
                id="name"
                name="name"
                required={true}
                autoFocus={true}
                onChange={(e) => setName(e.currentTarget.value)}
                error={getErrorsForInput(outcome, 'name')}
              />
            </FormSection>
            {
              !props.isNew &&
              <FormSection title="Redirect URI" htmlFor="redirectUri" outcome={outcome}>
                <TextInput
                  id="redirectUri"
                  name="redirectUri"
                  onChange={(e) => setRedirectUri(e.currentTarget.value)}
                  error={getErrorsForInput(outcome, 'redirectUri')}
                />
              </FormSection>
            }
            <FormSection title="Access Policy" htmlFor="accessPolicy" outcome={outcome}>
              <AccessPolicyInput name="accessPolicy" onChange={setAccessPolicy} />
            </FormSection>
            <FormSection title="Description" htmlFor="description" outcome={outcome}>
              <TextInput
                id="description"
                name="description"
                onChange={(e) => setDescription(e.currentTarget.value)}
                error={getErrorsForInput(outcome, 'description')}
              />
            </FormSection>
            <Group position="right">
              <Button type="submit">Create</Button>
              <Button type="button" variant="outline" onClick={()=>navigate(-1)}>Cancel</Button>
            </Group>
          </Stack>
        )}
        {client && (
          <div data-testid="success">
            <Text>Client created</Text>
            <List>
              <List.Item>
                <MedplumLink to={client}>Go to new client</MedplumLink>
              </List.Item>
              <List.Item>
                <MedplumLink to="/admin/clients">Back to clients list</MedplumLink>
              </List.Item>
            </List>
          </div>
        )}
      </Form>
      </Paper>
    </>
  );
}
